import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, ChevronRight, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { getServiceBySlug, getServices } from "@/data/services";
import { getFaqs } from "@/data/faqs";
import { SITE, imageProxy } from "@/data/site";
import { useLang } from "@/lib/i18n";
import QuoteForm from "@/components/site/QuoteForm";
import FAQ from "@/components/site/FAQ";
import ReadyCTA from "@/components/site/ReadyCTA";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const service = getServiceBySlug(slug, lang);

  if (!service) return <Navigate to="/services" replace />;

  const related = getServices(lang).filter((s) => s.slug !== service.slug).slice(0, 3);
  const faqs = getFaqs(service.slug, lang);
  const heroImg = service.image.startsWith("http")
    ? imageProxy(service.image, { w: 1200, q: 78 })
    : service.image;

  return (
    <>
      <section data-testid="service-hero" className="relative bg-brand-navy-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SITE.vanImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ filter: "blur(3px) brightness(0.72) saturate(1.1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark/78 via-brand-navy-dark/62 to-slate-900/78" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,191,0,0.10)_0%,transparent_55%)]" />
        </div>
        <div className="absolute -top-32 -right-24 w-[440px] h-[440px] rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-brand-navy-light/25 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
        <div className="relative container-max pt-6 pb-4">
          <nav className="text-xs text-white/60 flex items-center gap-1.5">
            <Link to="/" className="hover:text-white">{t.nav.home}</Link>
            <ChevronRight size={12} />
            <Link to="/services" className="hover:text-white">{t.nav.services}</Link>
            <ChevronRight size={12} />
            <span className="text-white font-medium">{service.name}</span>
          </nav>
        </div>
        <div className="relative container-max grid lg:grid-cols-2 gap-10 pb-14 pt-4 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
              {t.services.heroEyebrow}
            </div>
            <h1
              className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance"
              style={{ textShadow: "0 2px 16px rgba(0,0,0,0.5)" }}
            >
              {service.name}
            </h1>
            <p
              className="mt-5 text-lg text-white/95 leading-relaxed"
              style={{ textShadow: "0 1px 8px rgba(0,0,0,0.45)" }}
            >
              {service.summary}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a data-testid="service-call-cta" href={`tel:${SITE.primaryPhone}`}
                className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3.5 px-5 rounded-xl whitespace-nowrap shadow-lg shadow-yellow-300/30">
                <Phone size={18} />
                <span>{t.cta.call} {SITE.primaryPhone}</span>
              </a>
              <a href="#quote" data-testid="service-quote-cta"
                className="inline-flex items-center justify-center bg-white/15 backdrop-blur-md border-2 border-white/40 hover:bg-white hover:text-slate-900 text-white font-bold py-3.5 px-5 rounded-xl transition-colors whitespace-nowrap">
                {t.cta.freeEstimate}
              </a>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5">
                <Clock size={15} className="text-brand-yellow" />
                {lang === "es" ? "Disponible el mismo día" : "Same-day available"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={15} className="text-brand-yellow" /> {t.common.licensedInsured} #{SITE.license}
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl border-4 border-white/90">
              <img src={heroImg} alt={service.name} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-brand-yellow text-slate-900 rounded-2xl px-4 py-3 shadow-xl hidden sm:block">
              <p className="font-heading text-sm font-bold uppercase tracking-wider">Faster Plumbing</p>
              <p className="text-xs text-slate-700 mt-0.5">{t.common.southFlorida} · 12 {t.common.yrs}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="section-padding bg-white">
        <div className="container-max grid lg:grid-cols-2 gap-8">
          <article className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              {lang === "es" ? "El problema" : "The problem"}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              {lang === "es" ? "Lo que normalmente encontramos" : "What we usually find"}
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">{service.problem}</p>
          </article>
          <article className="bg-brand-navy text-white rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-yellow/20 blur-2xl" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow relative">
              {lang === "es" ? "Nuestro enfoque" : "Our approach"}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-2 relative">
              {lang === "es" ? "Cómo lo arreglamos" : "How we fix it"}
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed relative">{service.solution}</p>
          </article>
        </div>
      </section>

      {/* What's included */}
      <section className="section-padding bg-slate-50">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {lang === "es" ? "Qué incluye" : "What's included"}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
              {lang === "es" ? "Cada trabajo, siempre." : "Every job, every time."}
            </h2>
          </div>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <CheckCircle2 className="text-brand-yellow-dark shrink-0 mt-0.5" size={20} />
                <span className="text-slate-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {lang === "es" ? "Nuestro proceso" : "Our process"}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
              {lang === "es" ? "Tres pasos. Sin sorpresas." : "Three steps. No surprises."}
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {(lang === "es"
              ? [
                  { n: "01", t: "Llame o pida cotización", d: "Cuéntenos qué pasa. Diagnosticamos por teléfono primero." },
                  { n: "02", t: "Inspección y cotización en sitio", d: "Un plomero con licencia confirma el problema y le da una cotización gratis por escrito." },
                  { n: "03", t: "Lo arreglamos bien", d: "Hacemos el trabajo, probamos fugas y limpiamos antes de irnos." },
                ]
              : [
                  { n: "01", t: "Call or request an estimate", d: "Tell us what's happening. We diagnose over the phone first." },
                  { n: "02", t: "Inspect & estimate on-site", d: "A licensed plumber confirms the issue and gives you a free written estimate." },
                  { n: "03", t: "Fix it the right way", d: "We do the work, leak-test, and clean up before we leave." },
                ]).map((step) => (
              <div key={step.n} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
                <span className="font-heading text-5xl font-bold text-brand-yellow">{step.n}</span>
                <h3 className="mt-3 font-heading text-xl font-semibold text-slate-900">{step.t}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="quote" className="section-padding bg-slate-50">
        <div className="container-max grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {t.common.freeEstimateTag}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight">
              {lang === "es" ? `Pida cotización para ${service.name}.` : `Get an estimate for ${service.name}.`}
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              {lang === "es"
                ? "Sin compromiso. Sin ventas a presión. Solo una cotización clara y por escrito de un plomero con licencia en Florida."
                : "No obligation. No high-pressure sales. Just a clear, written estimate from a licensed Florida plumber."}
            </p>
          </div>
          <QuoteForm sourcePage={`service:${service.slug}`} />
        </div>
      </section>

      {/* Related */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {lang === "es" ? "Servicios relacionados" : "Related services"}
            </h2>
            <Link to="/services" className="text-sm font-semibold text-brand-navy hover:text-brand-yellow-dark">
              {t.cta.viewAll} →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-${s.slug}`}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <h3 className="font-heading text-lg font-semibold text-slate-900">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-500 line-clamp-2">{s.short}</p>
                <p className="mt-4 text-xs font-semibold text-brand-yellow-dark">{t.cta.learnMore} →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQ items={faqs} />

      <ReadyCTA />
    </>
  );
}
